/**
 * Dashboard Assistant Context
 * 
 * Provides context and state management for the Dashboard Assistant
 */

import React, { createContext, useContext, useState, useCallback } from 'react';
import { getDashboardContextualHelp, COMMON_DASHBOARD_QUESTIONS } from './dashboardKnowledge';

// Create context
const DashboardAssistantContext = createContext(null);

// Provider component
export const DashboardAssistantProvider = ({ children }) => {
  const [conversationHistory, setConversationHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  
  // Add a message to conversation history
  const addMessage = useCallback((message) => {
    setConversationHistory(prev => [...prev, {
      ...message,
      timestamp: new Date().toISOString()
    }]);
  }, []);
  
  // Clear conversation history
  const clearConversation = useCallback(() => {
    setConversationHistory([]);
  }, []);
  
  // Get contextual help
  const getContextualHelp = useCallback(() => {
    return getDashboardContextualHelp();
  }, []);
  
  // Get full context for AI
  const getFullContext = useCallback(() => {
    return {
      page: 'dashboard',
      mode: 'dashboard',
      panels: [
        'Case Status Overview',
        'Upcoming Sessions',
        'User Journey',
        'Client Reviews',
        'Earnings Summary',
        'Platform Notifications'
      ]
    };
  }, []);
  
  const value = {
    conversationHistory,
    addMessage,
    clearConversation,
    isLoading,
    setIsLoading,
    getContextualHelp,
    getFullContext,
    knowledgeBase: COMMON_DASHBOARD_QUESTIONS
  };
  
  return (
    <DashboardAssistantContext.Provider value={value}>
      {children}
    </DashboardAssistantContext.Provider>
  );
};

// Hook to use the context
export const useDashboardAssistant = () => {
  const context = useContext(DashboardAssistantContext);
  if (!context) {
    throw new Error('useDashboardAssistant must be used within a DashboardAssistantProvider');
  }
  return context;
};

export default DashboardAssistantContext;

